
import { supabase, getWebsiteId } from './supabase';

// Tarayıcı bilgisini al
const getBrowser = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('SamsungBrowser')) return 'Samsung Browser';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  if (ua.includes('Trident')) return 'IE';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Diğer';
};

// Cihaz tipini al
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'Tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'Mobil';
  return 'Masaüstü';
};

// Referrer domain'i al
const getReferrerDomain = (): string => {
  if (!document.referrer) return 'Doğrudan';
  try {
    const url = new URL(document.referrer);
    if (url.hostname === window.location.hostname) return 'Dahili';
    if (url.hostname.includes('google')) return 'Google';
    if (url.hostname.includes('facebook') || url.hostname.includes('fb.')) return 'Facebook';
    if (url.hostname.includes('instagram')) return 'Instagram';
    if (url.hostname.includes('twitter') || url.hostname.includes('t.co')) return 'Twitter';
    if (url.hostname.includes('linkedin')) return 'LinkedIn';
    if (url.hostname.includes('youtube')) return 'YouTube';
    return url.hostname;
  } catch {
    return 'Bilinmiyor';
  }
};

// Session ID oluştur veya al
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Sayfa görüntüleme kaydet
export const trackPageView = async (pageTitle?: string): Promise<void> => {
  try {
    const websiteId = await getWebsiteId();
    if (!websiteId) return;

    // Admin sayfalarını takip etme
    if (window.location.pathname.startsWith('/admin')) return;

    await supabase.from('page_views').insert({
      website_id: websiteId,
      page_path: window.location.pathname,
      page_title: pageTitle || document.title,
      referrer: document.referrer || null,
      referrer_domain: getReferrerDomain(),
      device_type: getDeviceType(),
      browser: getBrowser(),
      session_id: getSessionId()
    });
  } catch (error) {
    console.error('Analytics error:', error);
  }
};

// İstatistikleri getir
export interface AnalyticsData {
  totalViews: number;
  uniqueSessions: number;
  todayViews: number;
  topPages: { page_path: string; page_title: string; count: number }[];
  referrerStats: { referrer_domain: string; count: number }[];
  deviceStats: { device_type: string; count: number }[];
  browserStats: { browser: string; count: number }[];
  dailyViews: { date: string; count: number }[];
}

export const getAnalytics = async (days: number = 30): Promise<AnalyticsData> => {
  const websiteId = await getWebsiteId();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Tüm verileri çek
  const { data: allViews } = await supabase
    .from('page_views')
    .select('*')
    .eq('website_id', websiteId)
    .gte('created_at', startDate.toISOString())
    .order('created_at', { ascending: false });

  const views = allViews || [];

  // Toplam görüntüleme
  const totalViews = views.length;

  // Benzersiz oturumlar
  const uniqueSessions = new Set(views.map(v => v.session_id)).size;

  // Bugünkü görüntülemeler
  const todayViews = views.filter(v => new Date(v.created_at) >= today).length;

  // En çok ziyaret edilen sayfalar
  const pageMap = new Map<string, { title: string; count: number }>();
  views.forEach(v => {
    const key = v.page_path;
    const existing = pageMap.get(key);
    if (existing) {
      existing.count++;
    } else {
      pageMap.set(key, { title: v.page_title || v.page_path, count: 1 });
    }
  });
  const topPages = Array.from(pageMap.entries())
    .map(([path, data]) => ({ page_path: path, page_title: data.title, count: data.count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Referrer istatistikleri
  const referrerMap = new Map<string, number>();
  views.forEach(v => {
    const domain = v.referrer_domain || 'Doğrudan';
    referrerMap.set(domain, (referrerMap.get(domain) || 0) + 1);
  });
  const referrerStats = Array.from(referrerMap.entries())
    .map(([domain, count]) => ({ referrer_domain: domain, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Cihaz istatistikleri
  const deviceMap = new Map<string, number>();
  views.forEach(v => {
    const device = v.device_type || 'Bilinmiyor';
    deviceMap.set(device, (deviceMap.get(device) || 0) + 1);
  });
  const deviceStats = Array.from(deviceMap.entries())
    .map(([device, count]) => ({ device_type: device, count }))
    .sort((a, b) => b.count - a.count);

  // Tarayıcı istatistikleri
  const browserMap = new Map<string, number>();
  views.forEach(v => {
    const browser = v.browser || 'Bilinmiyor';
    browserMap.set(browser, (browserMap.get(browser) || 0) + 1);
  });
  const browserStats = Array.from(browserMap.entries())
    .map(([browser, count]) => ({ browser, count }))
    .sort((a, b) => b.count - a.count);

  // Günlük görüntülemeler
  const dailyMap = new Map<string, number>();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    dailyMap.set(dateStr, 0);
  }
  views.forEach(v => {
    const dateStr = new Date(v.created_at).toISOString().split('T')[0];
    if (dailyMap.has(dateStr)) {
      dailyMap.set(dateStr, (dailyMap.get(dateStr) || 0) + 1);
    }
  });
  const dailyViews = Array.from(dailyMap.entries())
    .map(([date, count]) => ({ date, count }));

  return {
    totalViews,
    uniqueSessions,
    todayViews,
    topPages,
    referrerStats,
    deviceStats,
    browserStats,
    dailyViews
  };
};
